import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import { CreateFolderDialogProps } from '../types';
import { useTranslation } from '../../../i18n';

const CreateFolderDialog: React.FC<CreateFolderDialogProps> = ({
  open,
  newFolderName,
  onClose,
  onConfirm,
  onNameChange,
}) => {
  const { t } = useTranslation();
  const [validationError, setValidationError] = useState<string>('');

  // Validate folder name in real-time
  useEffect(() => {
    const validateFolderName = (name: string) => {
      const trimmedName = name.trim();

      if (!trimmedName) {
        setValidationError('');
        return;
      }

      // Check for invalid patterns
      if (trimmedName.includes('..')) {
        setValidationError(t('fileManagement.folderNameInvalidDots'));
        return;
      }

      if (trimmedName.startsWith('/') || trimmedName.startsWith('\\')) {
        setValidationError(t('fileManagement.folderNameInvalidStart'));
        return;
      }

      // Forbid forward slashes in folder names
      if (trimmedName.includes('/')) {
        setValidationError(t('fileManagement.folderNameInvalidSlash'));
        return;
      }

      // Valid folder name
      setValidationError('');
    };

    validateFolderName(newFolderName);
  }, [newFolderName, t]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !validationError && newFolderName.trim()) {
      onConfirm();
    }
  };

  const isValidName = !validationError && newFolderName.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('fileManagement.createNewFolder')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('fileManagement.folderName')}
          fullWidth
          variant="outlined"
          value={newFolderName}
          onChange={(e) => onNameChange(e.target.value)}
          onKeyPress={handleKeyPress}
          error={!!validationError}
          helperText={validationError}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={onConfirm} variant="contained" disabled={!isValidName}>
          {t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFolderDialog;
